import { createMemo, createSignal, For, onCleanup, onMount, Show, type Component } from 'solid-js';

import {
  estimateLabelBox,
  labelStyle,
  layoutLabels,
  type LabelAnchor,
  type PlacedLabel,
} from '../game/filter';
import { KEYBINDS } from '../game/keyboard';
import { lootLabelFor } from './lootVisibility';
import { engagedInMelee, labelClickVerdict, labelPointerEvents } from './lootClick';
import type { GameStore } from '../state/store';

/**
 * The screen-space item labels, and the vacuum's affordance.
 *
 * This is the Solid.js half of the spec's ground-item rendering: the beam and the shard
 * are Babylon's, the label floating over them is this. Which items get one, in what
 * colour and at what size is the loot filter's verdict; where they end up is
 * `layoutLabels`, which is what stops a pack's worth of drops landing in one unreadable
 * pile.
 *
 * ## Four rules this component exists to obey
 *
 * - **The `<For>` depends on `ground()`, never on `anchors()`.** `anchors` is written
 *   every rendered frame, so a list keyed off it re-keys its `<For>` at display refresh
 *   rate — a great deal of DOM churn for a list that has not changed. The list changes
 *   at 30 Hz with the snapshot; the *positions* come from a memo each label reads
 *   inside itself, so a frame is two style writes per label. Same arrangement as
 *   `Nameplates` and `FloatingDamageOverlay`.
 * - **Nothing here is interactive.** Every label is `pointer-events: none` and the layer
 *   is too. The spec asks for hover-to-preview and click-to-collect on a label, and both
 *   are refused deliberately: a label is a DOM box floating over the middle of the play
 *   area, and `.hud-interactive` there would swallow clicks meant for the monsters
 *   standing among the loot — the exact failure that turned a 599 × 114 action bar into a
 *   485 × 221 block over the ground the player clicks. The drop already has a generous
 *   invisible collider in the 3D scene that takes the click *at the item*, which is where
 *   the player is aiming anyway, and `F` is the bulk answer.
 * - **A label the solver could not place is hidden, not squeezed.** `overflow` becomes a
 *   "+N more" count rather than a pile of overlapping text.
 * - **Only what a rule or the fallback says is visible gets a label** — *or* what the player is
 *   revealing with Alt, *or* what has just landed. `lootVisibility.ts` owns those three and this
 *   component asks it; a `Hide` rule still removes the label, and the drop is still on the floor.
 *   Alt can only ever *add*: a key that hid the rare you were walking towards would be
 *   indistinguishable from it despawning.
 * - **A drop with no label has no click target.** `game/loot.ts` reads the same verdict for
 *   `isPickable`, so clicking the floor where a hidden drop sits moves you rather than collecting
 *   something invisible. The two must never disagree, and `lootVisibility.test.ts` asserts it
 *   across every combination.
 */

/**
 * How far the nearest drop's label is lifted, in CSS pixels.
 *
 * `LootPrompt` hangs over the *same* anchor with its bottom on it, so without this the two
 * boxes land exactly on top of each other for whichever drop is closest — the one pile the
 * layout solver cannot see, because the prompt is not one of its anchors.
 *
 * **Measured, not chosen.** For a drop this component has labelled, the prompt is
 * hint-only — it no longer repeats a name the label is already showing, which is what made
 * the nearest drop read as being listed twice — and measures **104 × 25** at the reference
 * viewport. 30 is that height plus the solver's own `DEFAULT_GAP` and a pixel. Re-measure
 * if the prompt's padding, font size or contents change; the method is in
 * `doc/grind/architecture/TESTING-STRATEGY.md`. It was 34, against a two-line prompt of 29 that had since grown
 * to 44 in the rem conversion without anyone re-measuring — a stale clearance is a silent
 * overlap.
 *
 * Lifting the label rather than suppressing it keeps the filter's styling on every drop,
 * including the one the player is standing next to, which is the one they care about most.
 */
const PROMPT_CLEARANCE = 30;
export const LootLabels: Component<{ store: GameStore }> = (props) => {
  /**
   * Whether a drop belongs to somebody else.
   *
   * A monster rolls one drop per player who was in the fight, and everyone receives all of
   * them — there is one snapshot per tick, encoded once and broadcast. So the label is
   * where "not yours" gets said, and the server refuses the pickup regardless.
   *
   * **Dimmed, not hidden.** The same rule the loot filter follows and for a stronger
   * reason here: a drop that vanished for one player and not another would make the floor
   * disagree between two people standing on it, and the first thing anybody would do is
   * ask what happened to the sword they can see in the other person's screenshot.
   *
   * An absent owner is anyone's — an item dropped by hand, which is how a player hands
   * something over — and is never dimmed.
   */
  const theirs = (g: { ownerId?: string }) =>
    g.ownerId !== undefined && g.ownerId !== '' && g.ownerId !== props.store.selfId();

  /**
   * The drops that want a label, at 30 Hz.
   *
   * Deliberately free of `anchors()` — see the header. The verdict comes from the store's
   * own per-snapshot memo, so this is a filter over a list, not an evaluation.
   */
  const labelled = () => {
    return props.store
      .ground()
      .map((g) => {
        const verdict = props.store.filterVerdict(g.id);
        // Through `lootLabelFor`, which reads the store itself. `LootPrompt` asks the same
        // way and suppresses its duplicate of the name on the answer — it used to ask the
        // filter alone, which is why Alt made every nearby drop read twice.
        const shown = lootLabelFor(props.store, g);
        return { item: g, verdict, shown };
      })
      .filter(({ shown }) => shown.showLabel)
      .map(({ item, verdict, shown }) => ({
        item,
        shown,
        // A revealed or flashing drop has no filter verdict to style it — it is precisely the
        // case the filter declined to describe — so it takes the tier-4 presentation the beam
        // code already falls back to. `labelStyle` handles `undefined` for the same reason.
        style: labelStyle(verdict),
      }));
  };

  /**
   * Where each label goes, recomputed per frame.
   *
   * A memo over `anchors()` rather than part of the list above: the DOM structure must
   * not depend on a per-frame signal, but the *geometry* has to. A dozen boxes through a
   * pure solver is a rounding error next to the projection that produced the anchors.
   */
  const placed = createMemo<Map<string, PlacedLabel>>(() => {
    const anchors = props.store.anchors();
    const viewport = viewportSize();

    const nearest = props.store.nearestLoot()?.id;

    const wanted: LabelAnchor[] = labelled().map(({ item, style }) => {
      const anchor = anchors[item.id];
      const box = estimateLabelBox(item.name, style.fontSize);
      const lift = item.id === nearest ? PROMPT_CLEARANCE : 0;
      return {
        id: item.id,
        x: anchor?.x ?? Number.NaN,
        y: (anchor?.y ?? Number.NaN) - lift,
        width: box.width,
        height: box.height,
        tier: style.tier,
        // No anchor yet, or one behind the camera: hidden without being laid out, so an
        // off-screen drop cannot displace a label the player can actually read.
        visible: anchor?.visible === true,
      };
    });

    const layout = layoutLabels(wanted, { viewport });
    return new Map(layout.placed.map((p) => [p.id, p]));
  });

  /** How many wanted a label and did not get one. */
  const overflow = () => labelled().length - placed().size;

  /*
    Phase 37 — labels take clicks, but only when the precedence rule says they win.

    **Alt is tracked rather than read at click time**, because the decision has to be a *style*:
    a label that should lose is left `pointer-events: none` so the click reaches Babylon by the
    browser's own rules. Reading `altKey` inside a handler would mean the label had already
    swallowed the event and would have to re-dispatch it — a second event path to keep in step
    with picking, which is the first.

    Cleared on blur as well as keyup: a player who alt-tabs releases the key somewhere this
    window never hears about, and a stuck Alt would silently make every label win every click.
  */
  const [altHeld, setAltHeld] = createSignal(false);
  onMount(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'Alt') setAltHeld(true); };
    const up = (e: KeyboardEvent) => { if (e.key === 'Alt') setAltHeld(false); };
    const blur = () => setAltHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    onCleanup(() => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    });
  });

  // Weapon reach is `Derived.attackRange` — the server's number, which a bow raises. See
  // `engagedInMelee` for why that is the right reading of "in melee".
  const engaged = createMemo(() =>
    engagedInMelee(props.store.self(), props.store.target(), props.store.character()?.derived.attackRange ?? 2),
  );

  return (
    <div class="loot-labels" data-testid="loot-labels">
      <For each={labelled()}>
        {(entry) => {
          const box = () => placed().get(entry.item.id);
          const rarity = entry.item.rarity.toLowerCase();
          const verdict = () =>
            labelClickVerdict({
              // A label the filter is dimming to nothing is not on screen to be clicked.
              shown: entry.shown.opacity > 0,
              altHeld: altHeld(),
              engaged: engaged(),
            });
          return (
            <Show when={box()}>
              {(at) => (
                <div
                  class={`loot-label loot-label--tier${entry.style.tier} loot-label--${rarity}`}
                  
                  classList={{ 'loot-label--theirs': theirs(entry.item) }}
                  data-testid={`loot-label-${entry.item.id}`}
                  data-tier={entry.style.tier}
                  data-shown={entry.shown.reason}
                  data-theirs={theirs(entry.item) ? 'true' : undefined}
                  data-verdict={verdict()}
                  onClick={() => {
                    // Only ever reached when the verdict is `pickup`: anything else left the
                    // label transparent to the pointer and this handler was never a candidate.
                    // The server re-checks ownership, range and bag space and refuses with a
                    // spoken reason, so nothing here needs to guess at any of the three.
                    // Walk to it, then take it — the path a click on the item takes. Sent as a
                    // bare pickup, it did nothing from further than `PickupRange` away.
                    props.store.collect(entry.item.id);
                  }}
                  style={{
                    'pointer-events': labelPointerEvents(verdict()),
                    left: `${at().left}px`,
                    top: `${at().top}px`,
                    'font-size': `${entry.style.fontSize}px`,
                    opacity: String(entry.shown.opacity),
                    // Null means "keep the rarity token the class already set", which is
                    // why these are conditional rather than defaulted — a hardcoded
                    // fallback here would be a second palette next to tokens.css.
                    ...(entry.style.textColour ? { color: entry.style.textColour } : {}),
                    ...(entry.style.borderColour
                      ? { 'border-color': entry.style.borderColour }
                      : {}),
                  }}
                >
                  {entry.item.name}
                </div>
              )}
            </Show>
          );
        }}
      </For>

      <Show when={overflow() > 0}>
        <div class="loot-labels__overflow" data-testid="loot-overflow">
          +{overflow()} more on the floor
        </div>
      </Show>

      <VacuumHint store={props.store} />
    </div>
  );
};

/**
 * The `F` prompt, shown only when pressing it would do something.
 *
 * A keybind nobody is told about may as well not exist — the same reason `HudMenu`
 * exists — but an always-visible prompt for a key that usually does nothing is worse than
 * none. So it appears exactly when the filter wants something within the vacuum's reach,
 * and says how many, which is also the only feedback a sweep gets.
 *
 * Not interactive: it prints a key rather than being a button. A button here would be a
 * `.hud-interactive` box low on the screen, which is the one place a HUD box must not be.
 */
export const VacuumHint: Component<{ store: GameStore }> = (props) => {
  const count = () => props.store.vacuumCount();
  return (
    <Show when={count() > 0}>
      <div class="loot-vacuum" data-testid="vacuum-hint">
        <kbd class="loot-vacuum__key">{KEYBINDS.vacuum.toUpperCase()}</kbd>
        <span class="loot-vacuum__text">
          sweep {count()} {count() === 1 ? 'drop' : 'drops'}
        </span>
      </div>
    </Show>
  );
};

/**
 * The area labels must stay inside.
 *
 * `window` is read rather than the canvas measured: the HUD is a full-viewport overlay
 * and the anchors are already in CSS pixels of it. Falls back to the solver's own default
 * where there is no window, which is jsdom before a size is set.
 */
function viewportSize(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1920, height: 1080 };
  return {
    width: window.innerWidth || 1920,
    height: window.innerHeight || 1080,
  };
}
